/**
 * Utilities for getting court and coach IDs from a lesson.
 * Handles both legacy (single courtId/coachId) and new (courtIds/coachIds arrays) formats.
 */

export function getLessonCourtIds(
  lesson: { courtIds?: string[]; courtId?: string }
): string[] {
  if (lesson.courtIds?.length) return lesson.courtIds;
  if (lesson.courtId) return [lesson.courtId];
  return [];
}

export function getLessonCoachIds(
  lesson: { coachIds?: string[]; coachId?: string }
): string[] {
  if (lesson.coachIds?.length) return lesson.coachIds;
  if (lesson.coachId) return [lesson.coachId];
  return [];
}

/** First court ID for display when only one is needed */
export function getPrimaryCourtId(
  lesson: { courtIds?: string[]; courtId?: string }
): string | undefined {
  const ids = getLessonCourtIds(lesson);
  return ids[0];
}

/** First coach ID for display when only one is needed */
export function getPrimaryCoachId(
  lesson: { coachIds?: string[]; coachId?: string }
): string | undefined {
  const ids = getLessonCoachIds(lesson);
  return ids[0];
}

/** Coach roster for one occurrence: session override when set, otherwise lesson default. */
export function resolveSessionCoachIds(
  lesson: { coachIds?: string[]; coachId?: string },
  session?: { coachIds?: string[] } | null
): string[] {
  if (session?.coachIds && session.coachIds.length > 0) {
    return session.coachIds;
  }
  return getLessonCoachIds(lesson);
}

/** Court allocation for one occurrence: session override when set, otherwise lesson default. */
export function resolveSessionCourtIds(
  lesson: { courtIds?: string[]; courtId?: string },
  session?: { courtIds?: string[]; hasCourtOverride?: boolean } | null
): string[] {
  if (session?.hasCourtOverride === true && session.courtIds) {
    return session.courtIds;
  }
  if (session?.hasCourtOverride === false) {
    return getLessonCourtIds(lesson);
  }
  // Legacy callers without hasCourtOverride: treat non-empty session.courtIds as override rows.
  if (session?.courtIds && session.courtIds.length > 0) {
    return session.courtIds;
  }
  return getLessonCourtIds(lesson);
}

/** Whether a coach id appears on a resolved session roster (domain or composite id). */
export function isCoachIdOnRoster(
  coachId: string,
  academyId: string,
  rosterCoachIds: string[]
): boolean {
  const compositeCoachId = `${coachId}@${academyId}`;
  return rosterCoachIds.includes(coachId) || rosterCoachIds.includes(compositeCoachId);
}

/** Whether a coach (domain id) is assigned to a lesson session, including per-session replacements. */
export function isCoachAssignedToLessonSession(
  coachId: string,
  academyId: string,
  lesson: { coachIds?: string[]; coachId?: string },
  session?: { coachIds?: string[] } | null
): boolean {
  const roster = resolveSessionCoachIds(lesson, session);
  return isCoachIdOnRoster(coachId, academyId, roster);
}
